<template>
    <card class="spaced typo" v-if="loaded">

        <div class="pt-2" v-if="ctrl.rows.length">

            <h2 class="text-white mb-4">{{ $t('xapi.common.agents') }}</h2>

            <base-table :data="ctrl.rows" :columns="columns">

                <template slot="columns">
                    <th>{{ $t('xapi.common.name') }}</th>
                    <th>{{ $t('xapi.common.id') }}</th>
                    <th></th>
                </template>

                <template slot-scope="{row}">
                    <td :class="cellClass(row)">
                        {{ row.name }}
                    </td>

                    <td :class="cellClass(row)">
                        <span class="font-weight-bold">{{ row.info.type }}</span>
                        <div v-if="row.info.part1"><small>{{ row.info.part1 }}</small></div>
                        <div v-if="row.info.part2"><small>{{ row.info.part2 }}</small></div>
                    </td>

                    <td :class="cellClass(row)" class="td-actions text-right">

                        <base-button icon link size="md" type="default" disabled v-if="row.deleted_at">
                            <i class="tim-icons icon-recycling"></i>
                        </base-button>

                        <base-button icon link size="md" type="danger" @click="ctrl.delete(row.id)" v-else>
                            <i class="tim-icons icon-simple-remove"></i>
                        </base-button>
                        
                    </td>
                </template>

            </base-table>
        </div>

        <p class="text-center py-3" v-else>{{ $t('xapi.agents.no-agent') }}</p>
                
        <div class="text-center mt-4 mb-4">
            <base-button type="primary" @click="ctrl.create()">
                {{ $t('xapi.agents.new') }}
            </base-button>
        </div>

        <div class="text-center">

            <base-button link size="md" type="danger" @click="destroyUserAgents()" v-if="hasAgentsToDestroy">
                {{ $t('xapi.agents.destroy-user-agents') }}
            </base-button>
            
        </div>

        <portal to="modals">

            <modal class="no-footer" :title="$t('xapi.common.agent')" v-model="ctrl.modals.edit" :loading="ctrl.form.loading">
                <agent-form v-model="ctrl.form.data" :errors="ctrl.form.errors">
                </agent-form>
            </modal>

            <modal-confirm v-model="ctrl.modals.confirmDelete">
                <p>{{ $t('xapi.agents.remove-from-user-confirm') }}</p>
            </modal-confirm>

            <modal-confirm v-model="ctrl.modals.confirmDestroy">
                <p>{{ $t('xapi.agents.destroy-user-agents-confirm') }}</p>
            </modal-confirm>

        </portal>

    </card>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import AgentForm from './AgentForm'

    export default {

        components: {
            AgentForm
        },

        data() {
            return {
                loaded: false,
                ctrl: new CrudController({
                    endpoint: '/trax/api/front/privacy/users/' + this.$route.params.id + '/agents',
                    mapData: this.mapData,
                    unmapData: this.unmapData,
                    modals: ['confirmDestroy']
                }),
                columns: ['name', 'id'],
            }
        },

        created() {
            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
            })
        },

        computed: {

            hasAgentsToDestroy() {
                return this.ctrl.rows.filter(row => !row.deleted_at).length > 0
            }
        },

        methods: {

            destroyUserAgents() {
                this.ctrl.modals.open('confirmDestroy').then(resp => {
                    if (resp.confirmed) {
                        axios.delete(
                            '/trax/api/front/privacy/users/' + this.$route.params.id + '/destroy',
                        )
                        .then(response => {
                            this.ctrl.list()
                        })
                        .catch(error => {
                            if (err.response.status == 403) {
                                this.$notify({type: 'danger', message: this.$t('errors.403-delete') })
                            } else {
                                this.$auth.handleErrorVm(error, this)
                            }
                        });
                    }
                })
            },
            
            mapData(item) {
                return {
                    id: item ? item.id : null,
                    name: item ? this.name(item) : null,
                    info: item ? this.info(item) : null,
                    deleted_at: item ? item.deleted_at : null,
                    agent: null
                }
            },

            unmapData(data) {
                return {
                    agent: JSON.stringify(this.fieldToXapiAgent(data.agent))
                }
            },

            fieldToXapiAgent(agent) {
                if (!agent) {
                    return
                }
                if (agent.startsWith('account:')) {
                    let parts = agent.substring(8).split('@')
                    return {
                        account: {
                            name: parts[0],
                            homePage: parts[1],
                        }
                    }
                }
                if (agent.startsWith('sha1sum:')) {
                    return {
                        mbox_sha1sum: agent.substring(8)
                    }
                }
                if (agent.startsWith('openid:')) {
                    return {
                        openid: agent.substring(7)
                    }
                }
                return {
                    mbox: 'mailto:' + agent
                }
            },

            agentToXapiAgent(item) {
                let parts = item.vid.split('::')
                let agent = {
                    objectType: item.is_group ? 'Group' : 'Agent',
                }
                if (item.name) {
                    agent.name = item.name
                }
                if (parts[0] == 'account') {
                    let accountParts = parts[1].split('@')
                    agent[parts[0]] = {
                        name: accountParts[0],
                        homePage: accountParts[1]
                    }
                } else {
                    agent[parts[0]] = parts[1]
                }
                return agent
            },

            name(item) {
                let agent = this.agentToXapiAgent(item)
                return agent.name ? agent.name : ''
            },

            info(item) {
                let agent = this.agentToXapiAgent(item)
                let info = {}
                if (agent.mbox) {
                    info.type = 'mbox'
                    info.part1 = String(agent.mbox).substr(7)
                } else if (agent.account) {
                    info.type = 'account'
                    info.part1 = agent.account.name
                    info.part2 = agent.account.homePage
                } else if (agent.openid) {
                    info.type = 'openid'
                    info.part1 = agent.openid
                } else {
                    info.type = 'mbox_sha1sum'
                    info.part1 = agent.mbox_sha1sum
                }
                return info
            },

            cellClass(row) {
                return row.deleted_at ? 'disabled' : ''
            }
        }
    }
</script>

<style scoped>

    td.disabled, td.disabled small, td.disabled .btn.btn-link {
        color: #777 !important;
    }

</style>
